import React, { useState, useEffect } from "react";
import {
  Table,
  Col,
  Button,
  Row,
  Form,
  InputGroup,
  FormControl,
  Pagination,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FileAttachment } from "./types";
import VariantImage from "./VariantImage";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface ProductVariantsTableProps {
  variantsData: any[];
  selectedAttributes: {
    attribute_code: string;
    frontend_label: string;
    is_imaged?: boolean;
  }[];
  onUpdateVariant: (updatedVariants: any[]) => void;
  formValues: { [key: string]: any };
  onUpdateVariantImage: (
    imagedAttributeCode: string,
    imagedAttributeValue: string,
    newImage: FileAttachment
  ) => void;
  tableHeaderRef?: React.Ref<HTMLTableSectionElement>;
}

// Simple pagination component
const TablePagination = ({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) => {
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  return (
    <Pagination className="justify-content-end mt-3">
      <Pagination.First onClick={() => handlePageChange(1)} />
      <Pagination.Prev
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      />
      <Pagination.Item active>{currentPage}</Pagination.Item>
      <Pagination.Next
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      />
      <Pagination.Last onClick={() => handlePageChange(totalPages)} />
    </Pagination>
  );
};

const ProductVariantsTable: React.FC<ProductVariantsTableProps> = ({
  variantsData: initialVariantsData,
  selectedAttributes,
  onUpdateVariant,
  formValues,
  onUpdateVariantImage,
  tableHeaderRef, // Use the parent’s ref
}) => {
  const [variantsData, setVariantsData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const navigate = useNavigate();

  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const [masterPrice, setMasterPrice] = useState("");
  const [masterQuantity, setMasterQuantity] = useState("");

  const { t } = useTranslation();

  // Detect if there's an "imaged" attribute
  const imagedAttributeCode = selectedAttributes.find(
    (attr) => attr.is_imaged
  )?.attribute_code;

  const isExpedieParWamia =
    formValues.mode_expedition?.[0]?.option_id === "16500";

  useEffect(() => {
    // If we have an "imaged" attribute, sort data by that attribute value
    if (imagedAttributeCode) {
      const sortedData = [...initialVariantsData].sort((a, b) => {
        const aValue = a[imagedAttributeCode]?.value || "";
        const bValue = b[imagedAttributeCode]?.value || "";
        return aValue.localeCompare(bValue);
      });
      setVariantsData(sortedData);
    } else {
      setVariantsData([...initialVariantsData]);
    }
  }, [initialVariantsData, imagedAttributeCode]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, variantsData.length);
  const paginatedVariants = variantsData.slice(startIndex, endIndex);
  const totalPages = Math.ceil(variantsData.length / itemsPerPage);

  // Selection logic
  const handleItemSelection = (id: string) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );
  };

  const handleMasterCheckbox = () => {
    const allSelected = paginatedVariants.every((variant) =>
      selectedItems.includes(variant.id)
    );
    if (allSelected) {
      // deselect
      setSelectedItems((prev) =>
        prev.filter(
          (id) => !paginatedVariants.some((variant) => variant.id === id)
        )
      );
    } else {
      // select all on page
      const newSelections = paginatedVariants.map((variant) => variant.id);
      setSelectedItems((prev) => [
        ...prev,
        ...newSelections.filter((id) => !prev.includes(id)),
      ]);
    }
  };

  const handleSelectAllItems = () => {
    if (selectedItems.length === variantsData.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(variantsData.map((v) => v.id));
    }
  };

  // Rowspan for identical "imaged" attribute values
  const getRowSpanCount = (index: number) => {
    if (!imagedAttributeCode) return 1;
    let count = 1;
    for (let i = index + 1; i < variantsData.length; i++) {
      if (
        variantsData[i][imagedAttributeCode]?.option_id ===
        variantsData[index][imagedAttributeCode]?.option_id
      ) {
        count++;
      } else {
        break;
      }
    }
    return count;
  };

  // Apply master price/qty to selected items or all if none are selected
  const handleApplyToAll = () => {
    const updatedVariants = variantsData.map((variant) => {
      if (selectedItems.length === 0 || selectedItems.includes(variant.id)) {
        return {
          ...variant,
          price: masterPrice ? parseFloat(masterPrice) : variant.price,
          quantity: masterQuantity ? parseInt(masterQuantity, 10) : variant.quantity,
        };
      }
      return variant;
    });
  
    setVariantsData(updatedVariants);
    onUpdateVariant(updatedVariants); // ✅ Ensure updates are passed up
  };  

  // Updating images for variants that share the same "imagedAttributeValue"
  const handleUpdateVariantImageLocal = (
    variant: any,
    newImage: FileAttachment
  ) => {
    if (!imagedAttributeCode) {
      console.error("No imaged attribute code is defined.");
      return; // Early return if imagedAttributeCode is not defined
    }
    const imagedAttributeValue = variant[imagedAttributeCode]?.value;
    const updatedVariants = variantsData.map((v) => {
      if (v[imagedAttributeCode]?.value === imagedAttributeValue) {
        return { ...v, image: newImage };
      }
      return v;
    });
    setVariantsData(updatedVariants);
    onUpdateVariantImage(imagedAttributeCode, imagedAttributeValue, newImage);
  };

  // Utility function to create a composite key
  const createCompositeKey = (variant: any) => {
    return selectedAttributes
      .map((attr) => variant[attr.attribute_code]?.option_id || "")
      .join("-");
  };



  const handlePriceChange = (variantIndex: number, value: string) => {
    const updatedVariants = [...variantsData]; // Copy array to avoid state mutation
    updatedVariants[variantIndex] = {
      ...updatedVariants[variantIndex],
      price: parseFloat(value) || 0,
    };
  
    setVariantsData(updatedVariants);
    onUpdateVariant(updatedVariants); // ✅ Ensure updates are passed up
  };
  
  const handleQuantityChange = (variantIndex: number, value: string) => {
    const updatedVariants = [...variantsData]; // Copy array to avoid state mutation
    updatedVariants[variantIndex] = {
      ...updatedVariants[variantIndex],
      quantity: parseInt(value, 10) || 0,
    };
  
    setVariantsData(updatedVariants);
    onUpdateVariant(updatedVariants); // ✅ Ensure updates are passed up
  };
      

  // Building table columns dynamically
  const columns = [
    {
      id: "select",
      header: (
        <input
          type="checkbox"
          checked={paginatedVariants.every((variant) =>
            selectedItems.includes(variant.id)
          )}
          onChange={handleMasterCheckbox}
          style={{ width: "100%" }}
        />
      ),
      cell: (variant: any) => (
        <td
          key={`select-${variant.id}`}
          style={{ width: "48px", textAlign: "center" }}
        >
          <input
            type="checkbox"
            checked={selectedItems.includes(variant.id)}
            onChange={() => handleItemSelection(variant.id)}
          />
        </td>
      ),
    },
    ...(imagedAttributeCode
      ? [
          // Image column
          {
            id: "image",
            header: t("table.header.image"),
            cell: (variant: any, rowIndex: number) => {
              const rowSpan = getRowSpanCount(rowIndex);
              // Only render once for each group of same imagedAttributeValue
              return rowIndex === 0 ||
                variantsData[rowIndex][imagedAttributeCode]?.option_id !==
                  variantsData[rowIndex - 1][imagedAttributeCode]?.option_id ? (
                <td
                  key={`image-${variant.id}`}
                  rowSpan={rowSpan}
                  style={{ textAlign: "center" }}
                >
                  <VariantImage
                    image={variant.image}
                    onImageUpload={(newImage) =>
                      handleUpdateVariantImageLocal(variant, newImage)
                    }
                  />
                </td>
              ) : null;
            },
          },
          // Additional columns for "imaged" attributes
          ...selectedAttributes
            .filter((attr) => attr.is_imaged)
            .map((attr) => ({
              id: attr.attribute_code,
              header: t(attr.frontend_label),
              cell: (variant: any, rowIndex: number) => {
                const opt = variant[attr.attribute_code] || {};
                const rowSpan = getRowSpanCount(rowIndex);
                return rowIndex === 0 ||
                  variantsData[rowIndex][attr.attribute_code]?.option_id !==
                    variantsData[rowIndex - 1][attr.attribute_code]
                      ?.option_id ? (
                  <td
                    key={`${attr.attribute_code}-${variant.id}`}
                    rowSpan={rowSpan}
                  >
                    <div
                      className="d-flex align-items-center"
                      style={{
                        backgroundColor: "var(--phoenix-choices-item-bg)",
                        borderRadius: "5px",
                        padding: "4px 8px",
                        width: "fit-content",
                      }}
                    >
                      {opt.swatch && (
                        <div
                          style={{
                            backgroundColor: opt.swatch,
                            width: "16px",
                            height: "16px",
                            marginRight: "8px",
                            border: "1px solid #ccc",
                          }}
                        />
                      )}
                      <span>{opt.value || ""}</span>
                    </div>
                  </td>
                ) : null;
              },
            })),
        ]
      : []),
    // Non-imaged attribute columns
    ...selectedAttributes
      .filter((attr) => !attr.is_imaged)
      .map((attr) => ({
        id: attr.attribute_code,
        header: t(attr.frontend_label),
        cell: (variant: any) => {
          const opt = variant[attr.attribute_code] || {};
          return (
            <td key={`${attr.attribute_code}-${variant.id}`}>
              <div
                className="d-flex align-items-center"
                style={{
                  backgroundColor: "var(--phoenix-choices-item-bg)",
                  borderRadius: "5px",
                  padding: "4px 8px",
                  width: "fit-content",
                }}
              >
                <span>{opt.value || ""}</span>
              </div>
            </td>
          );
        },
      })),
    // Price column
    {
      id: "price",
      header: t("table.header.price"),
      cell: (variant: any, index: number) => (
        <td key={`price-${index}`}>
          <InputGroup>
            <InputGroup.Text>{t("Dinar")}</InputGroup.Text>
            <FormControl
              type="number"
              size="sm"
              placeholder={t("table.placeholder.enterPrice")}
              value={variant.price}
              onChange={(e) => handlePriceChange(index, e.target.value)}
              style={{ maxWidth: "100px" }}
            />
          </InputGroup>
        </td>
      ),
    },
    // Quantity column (only if not "ExpedieParWamia")
    ...(isExpedieParWamia
      ? []
      : [
        {
          id: "quantity",
          header: t("table.header.quantity"),
          cell: (variant: any, index: number) => (
            <td key={`quantity-${index}`}>
              <Form.Control
                size="sm"
                type="number"
                placeholder={t("table.placeholder.enterQuantity")}
                value={variant.quantity}
                onChange={(e) => handleQuantityChange(index, e.target.value)}
                min="0"
                style={{ maxWidth: "80px" }}
              />
            </td>
          ),
        },
        ]),
      ];

  return (
    <div>
      <Col xs={12} xl={12}>
        {/* Master Price/Quantity Area */}
        <div className="d-flex justify-content-between align-items-center mb-3 w-100">
          <div className="d-flex w-100">
            <Form className="d-flex w-100">
              <InputGroup
                className="mr-2"
                style={{ maxWidth: "200px", marginRight: "8px" }}
              >
                <InputGroup.Text>{t("Dinar")}</InputGroup.Text>
                <FormControl
                  size="sm"
                  type="number"
                  placeholder="Enter price"
                  value={masterPrice}
                  onChange={(e) => setMasterPrice(e.target.value)}
                />
                {/* Attach parent's ref to the <h1> */}
              </InputGroup>
              {!isExpedieParWamia && (
                <Form.Control
                  type="number"
                  size="sm"
                  placeholder={t("table.placeholder.enterQuantity")}
                  value={masterQuantity}
                  onChange={(e) => setMasterQuantity(e.target.value)}
                  min="0"
                  className="mr-2"
                  style={{ maxWidth: "200px", marginRight: "10" }}
                />
              )}

              <div className="ml-auto" style={{ marginLeft: "10" }}>
                <Button
                  size="sm"
                  onClick={handleApplyToAll}
                  style={{ maxWidth: "200px", marginRight: "80px" }}
                >
                  {selectedItems.length > 0
                    ? t("table.actions.applyToSelected")
                    : t("table.actions.applyToAll")}
                </Button>
              </div>
            </Form>
          </div>
        </div>

        {/* Display a "select all" link if some are selected */}
        {selectedItems.length > 0 && (
          <div
            className="mb-2"
            style={{
              textDecoration: "underline",
              cursor: "pointer",
              textAlign: "left",
            }}
            onClick={handleSelectAllItems}
          >
            {`${selectedItems.length} / ${variantsData.length} Click to ${
              selectedItems.length === variantsData.length
                ? "deselect"
                : "select"
            } all items`}
          </div>
        )}

        <Col xs={12} xl={12} style={{ overflowX: "auto" }}>
          <Table striped bordered hover className="phoenix-table fs-9">
            <thead ref={tableHeaderRef}>
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.id}
                    style={column.id === "select" ? { width: "48px" } : {}}
                  >
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedVariants.map((variant, rowIndex) => (
                <tr
                  key={`variant-row-${variant.id || rowIndex}`}
                  style={{ cursor: "pointer" }} // ✅ Indicate clickable row
                  onClick={() => navigate(`/products/${variant.id}`)} // ✅ Navigate on click
                >
                  {columns.map((col) => col.cell(variant, rowIndex))}
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
      </Col>

      {/* Pagination Row */}
      <Row className="align-items-center">
        <Col xs={6}>
          <div className="text-muted">
            {t("table.pagination.showingRecords", {
              start: startIndex + 1,
              end: endIndex,
              total: variantsData.length,
            })}
          </div>
        </Col>
        <Col xs={6} className="d-flex justify-content-end">
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </Col>
      </Row>
    </div>
  );
};

export default ProductVariantsTable;
